<script lang="ts">
	import iconChevron from "@core/assets/icons/chevron.svg?raw";
	import type { Country } from "@core/schema/Country.js";
	import { Currency } from "@core/schema/Currency.js";
	import { EconomicSubjectVatType } from "@core/schema/EconomicSubjectVatType.js";
	import { ProductVariantType } from "@core/schema/ProductVariantType.js";
	import type { SubcategoryForCustomer } from "@core/schema/Subcategory.js";
	import { getPriceForCustomerOfProductVariant } from "@core/schema/utils/getPriceForCustomerOfProductVariant.js";
	import { getProductHeadingWithAttributes } from "@core/schema/utils/getProductHeadingWithAttributes.js";
	import { getProductOfVariant } from "@core/schema/utils/getProductOfVariant.js";
	import { getProductPrimaryMedia } from "@core/schema/utils/getProductPrimaryMedia.js";
	import { getProductStockAmount } from "@core/schema/utils/getProductStockAmount.js";
	import { formatPrice } from "@core/utils/formatPrice.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import PreloadedPrice from "./PreloadedPrice.svelte";
	import ProductB2bModal from "./ProductB2bModal.svelte";
	import type { ProductForCard } from "./ProductCard.js";
	import ProductCardCartOptions from "./ProductCardCartOptions.svelte";
	import ProductCardMedia from "./ProductCardMedia.svelte";
	import ProductCardMediaFallback from "./ProductCardMediaFallback.svelte";
	import ProductTags from "./ProductTags.svelte";
	import Formatted from "./common/Formatted.svelte";
	import Icon from "./common/Icon.svelte";
	import { getTagsForProductCard } from "./getTagsForProductCard.js";

	const { tenant } = getGlobalTemplateProps();
	const { product, subcategory }: { product: ProductForCard; subcategory: SubcategoryForCustomer } = $props();

	const primaryVariantId = $derived(product.variants[0]?.id);
	const productPrimaryVariant = $derived(primaryVariantId ? getProductOfVariant(product, primaryVariantId) : undefined);
	const mediaSrc = $derived(
		productPrimaryVariant ? getProductPrimaryMedia(productPrimaryVariant, product.attributes) : undefined,
	);

	let openDetailModal = $state(false);

	const heading = $derived(getProductHeadingWithAttributes(tenant.locale, product));
</script>

<div class="grid grid-cols-subgrid col-span-full p-2 md:pr-10 md:border border-[#F6F6F6] rounded-3xl">
	<button
		class="flex gap-4 md:gap-8 items-center text-left"
		onclick={(): void => {
			openDetailModal = true;
		}}
	>
		<div
			class="bg-[#F6F6F6] w-35 h-24 sm:w-45 sm:h-50 lg:w-67 lg:h-57 flex overflow-hidden flex-col justify-between p-4 rounded-2xl relative grow-0 shrink-0"
		>
			{#if productPrimaryVariant}
				<ProductTags tags={getTagsForProductCard({ product, locale: tenant.locale, subcategory })} />
				{#if mediaSrc}
					<ProductCardMedia {mediaSrc} />
				{:else}
					<ProductCardMediaFallback />
				{/if}
			{/if}
		</div>
		<div class="flex flex-col gap-1 lg:gap-2 grow shrink">
			<div class="font-antipol text-4 lg:text-6 text-fcrdarkgray flex items-center gap-2.5">
				<div>
					<Formatted text={heading} />
				</div>
				<Icon icon={iconChevron} class="size-6 -rotate-90" />
			</div>
			<div class="text-3/normal lg:text-3.5/normal text-fcrgray line-clamp-2">
				<Formatted text={product.shortDescription[tenant.locale]} />
			</div>
		</div>
	</button>
	<div class="grid grid-cols-3 lg:grid-cols-subgrid lg:col-span-3 gap-y-2 my-2 justify-start">
		{#each product.variants as variant (variant)}
			{@const productOfVariant = getProductOfVariant(product, variant.id)}
			<div
				class="grid grid-cols-[1fr_1fr_2fr] lg:grid-cols-subgrid col-span-full items-center font-medium text-fcrdarkgray sm:gap-2"
			>
				<div class="lg:text-right text-sm sm:text-base order-1 pr-2 sm:pr-7 shrink-0 sm:text-nowrap">
					{#if product.variantType !== ProductVariantType.None}
						<Formatted text={variant.label[tenant.locale]} />
					{/if}
				</div>
				<div class="w-42.5 flex items-center order-3 lg:order-2 ml-auto">
					<ProductCardCartOptions
						showZeroCount
						showToasts
						shrinkOnMobile
						product={productOfVariant}
						attributes={product.attributes}
						max={getProductStockAmount(product.stock, variant, product.attributes)}
					/>
				</div>
				<div class="text-right text-sm sm:text-base order-2 lg:order-3">
					<PreloadedPrice class="h-6">
						{#snippet children(currency: Currency, destinationCountry: Country | "outsideEu")}
							{@const productPrice = getPriceForCustomerOfProductVariant(
								productOfVariant,
								currency,
								destinationCountry,
								EconomicSubjectVatType.NotVatSubject,
							).withTax}
							{#if currency === Currency.CZK}
								<Formatted text={formatPrice(Math.ceil(productPrice), currency, tenant.locale, false)} />
							{:else}
								<Formatted text={formatPrice(productPrice, currency, tenant.locale, true)} />
							{/if}
						{/snippet}
					</PreloadedPrice>
				</div>
			</div>
		{/each}
	</div>
</div>

{#if productPrimaryVariant}
	<ProductB2bModal {product} {productPrimaryVariant} {subcategory} bind:show={openDetailModal} />
{/if}
